import { useCallback, type RefObject } from 'react';
import { useEvent } from 'react-use';
import { DROPDOWN_PORTAL_ID } from '../components/Form/Dropdown/tokens';
import { POPOVER_PORTAL_ID } from '../components/Popover/tokens';
import { useCurrentDocument } from './useCurrentDocument';
import { useDynamicCallback } from './useDynamicCallback';

export const useOnClickOutside = <T extends HTMLElement>(
  ref: RefObject<T | null>,
  handler?: (e: MouseEvent) => void
) => {
  const currentDocument = useCurrentDocument();

  const isClickingOutsideRef = useDynamicCallback((e: MouseEvent) => {
    // we ignore any clicks in an autocomplete dropdown
    // at some later time we can make this functionality more flexible. Maybe allow the user of this hook to pass a list of refs to check against.
    const autocompleteDropdown = currentDocument.getElementById(DROPDOWN_PORTAL_ID);
    if (autocompleteDropdown?.contains(e.target as Node)) {
      return false;
    }

    // Clicks inside the global popover portal (`POPOVER_PORTAL_ID`) — any Popover content, including
    // overflow menus like tab "X More" — should not count as outside-clicks for this hook's consumers
    // (dropdown poppers, modals, drawers, tab editors, etc.).
    const popoverPortal = currentDocument.getElementById(POPOVER_PORTAL_ID);
    if (popoverPortal?.contains(e.target as Node)) {
      return false;
    }

    // we ignore any clicks in tour popovers to prevent them from closing modals
    const tourStep = currentDocument.getElementById('tour-step');
    if (tourStep?.contains(e.target as Node)) {
      return false;
    }

    // normal case
    return !ref.current || !ref.current.contains(e.target as Node);
  });

  const onClick = useCallback(
    (e: any) => {
      if (isClickingOutsideRef(e)) {
        handler?.(e);
      }
    },
    [isClickingOutsideRef, handler]
  );

  useEvent('mousedown', handler ? onClick : null, currentDocument);
};
